import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LeftNavigationExpandableItem, LeftNavigationItem } from '@core/navigation/models/left-navigation/left-navigation-item.interface';
import { LeftNavigationItemComponent } from '@layout/navigation/components/left-navigation-item/left-navigation-item.component';
import { Router } from '@angular/router';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';
import { NgTemplateOutlet } from '@angular/common';
import { SpinnerComponent, toggleContentFadeIn, toggleContentFadeOut } from '@kaseya-design-components';
import { FeaturesDirective } from '@core/features/directives/features.directive';
import { Permission } from '@core/permissions/models/permission.enum';
import { Feature } from '@core/features/models/feature.enum';
import { ExternalLinkLoadingFacade } from '@core/navigation/facades/external-link-loading.facade';
import { Country } from '@core/locations/models/country.enum';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { CountryAccessDirective } from '@core/locations/directives/country-access.directive';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { NavigationStorageFacade } from '@core/navigation/facades/navigation-storage.facade';
import { focusAdjacentElement, isNotUndefined, MenuNavigationDirection } from '@kaseya-utils';

@Component({
  selector: 'itc-left-navigation-expandable-item',
  templateUrl: 'left-navigation-expandable-item.component.html',
  styleUrl: 'left-navigation-expandable-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [toggleContentFadeIn, toggleContentFadeOut],
  imports: [
    TranslatePipe,
    LeftNavigationItemComponent,
    NgTemplateOutlet,
    FeaturesDirective,
    PermissionDirective,
    SpinnerComponent,
    CountryAccessDirective,
  ],
})
export class LeftNavigationExpandableItemComponent implements OnChanges {
  private readonly navigationItemChildren = computed(() => this.navigationItem().children);
  private readonly router = inject(Router);
  private readonly navigationFacade = inject(NavigationFacade);
  private readonly externalLinkLoadingFacade = inject(ExternalLinkLoadingFacade);
  private readonly navigationStorageFacade = inject(NavigationStorageFacade);
  private readonly isFlyoutMode = computed(() => this.isMenuCollapsed() && !this.isMobileView());
  private readonly menuButton = viewChild<ElementRef<HTMLButtonElement>>('menuButton');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  readonly navigationItem = input.required<LeftNavigationExpandableItem>();
  readonly isMenuCollapsed = input.required<boolean>();
  readonly topNavigation = input<TopNavigationPath>();
  readonly isMobileView = input(false);
  readonly isNavigationItemCollapsed = signal(true);
  readonly isChildItemActive = computed(() =>
    this.navigationItemChildren().some(({ id }) => this.navigationFacade.activeLeftNavigation() === id)
  );
  readonly isChildItemLoading = computed(() =>
    this.navigationItemChildren().some(({ id }) => this.externalLinkLoadingFacade.isExternalLinkLoading(id))
  );

  readonly navigationItemPermissions = computed(() => this.getNavigationAccessItems<Permission>('permission'));
  readonly navigationItemFeatures = computed(() => this.getNavigationAccessItems<Feature>('feature'));
  readonly navigationItemAccessibleCountries = computed(() => this.getNavigationAccessItems<Country[]>('accessibleCountries')?.flat());
  readonly isNavigationPanelShown = signal(false);
  readonly MenuNavigationDirection = MenuNavigationDirection;
  readonly navigationItemClick = output<void>();
  readonly isExpanded = computed(() => (this.isFlyoutMode() ? this.isNavigationPanelShown() : !this.isNavigationItemCollapsed()));

  ngOnChanges({ isMenuCollapsed, isMobileView }: SimpleChanges): void {
    if (!this.isMobileView()) {
      this.initNavigationItemCollapsedState();
    }

    if (isMenuCollapsed || isMobileView) {
      this.handleNavigationIfChildItemIsActive();
    }
  }

  toggleCollapse(): void {
    this.isNavigationItemCollapsed.update(value => !value);

    if (!this.isMobileView()) {
      this.navigationStorageFacade.setNavigationItemCollapsedState(this.navigationItem().id, this.isNavigationItemCollapsed());
    }
  }

  showNavigationPanel(): void {
    if (this.isMenuCollapsed()) {
      this.isNavigationPanelShown.set(true);
    }
  }

  hideNavigationPanel(): void {
    if (this.isNavigationPanelShown()) {
      this.isNavigationPanelShown.set(false);
    }
  }

  hideNavigationPanelOnMouseLeave(): void {
    const panel = this.panel()?.nativeElement;

    if (!panel?.contains(panel.ownerDocument.activeElement)) {
      this.hideNavigationPanel();
    }
  }

  handleMenuClick(): void {
    if (this.isFlyoutMode()) {
      this.showNavigationPanel();
    } else {
      this.toggleCollapse();
    }
  }

  closeNavigationPanelFromKeyboard(): void {
    if (!this.isNavigationPanelShown()) {
      return;
    }

    this.hideNavigationPanel();
    this.menuButton()?.nativeElement.focus();
  }

  hideNavigationPanelOnFocusOut({ relatedTarget, currentTarget }: FocusEvent): void {
    if (relatedTarget && !(currentTarget as HTMLElement).contains(relatedTarget as Node)) {
      this.hideNavigationPanel();
    }
  }

  moveFocus(event: Event, direction: MenuNavigationDirection): void {
    event.preventDefault();
    focusAdjacentElement(Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('a[href], button')), direction);
  }

  private handleNavigationIfChildItemIsActive(): void {
    const activeChildItem = this.navigationItem().children.find(({ id }) =>
      this.router.isActive(this.navigationFacade.getRouterLinkById(id) as string, {
        paths: 'subset',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      })
    );

    if (!activeChildItem) {
      return;
    }

    this.navigationFacade.setActiveLeftNavigation(activeChildItem.id);
    this.isNavigationItemCollapsed.set(false);
  }

  private getNavigationAccessItems<T extends Permission | Feature | Country[]>(key: keyof LeftNavigationItem): T[] | undefined {
    const accessItems = this.navigationItemChildren()
      .filter(item => isNotUndefined(item[key]))
      .map(item => item[key] as T);

    return accessItems.length >= this.navigationItemChildren().length ? accessItems : undefined;
  }

  private initNavigationItemCollapsedState(): void {
    const isCollapsed = this.navigationStorageFacade.isNavigationItemCollapsed(this.navigationItem().id);

    this.isNavigationItemCollapsed.set(isCollapsed);
  }
}
